import { useCallback, useEffect } from 'react';
import { useLocalStorage } from 'react-use';

import { toNumber } from 'utils/numbers';
import { hasValue } from 'utils/values';

const AUTH_REDIRECT_ATTEMPT_LOCALSTORAGE_KEY = 'authRedirectAttempt';

/**
 * Keep track of the number of times the user has been redirected to the authentication page.
 * Stops redirecting after the maxAttempt is reached.
 */
export function useRedirectWithLimit(maxAttempt = 3) {
  const [value, setValue] = useLocalStorage(AUTH_REDIRECT_ATTEMPT_LOCALSTORAGE_KEY, '0');

  const shouldRedirect = toNumber(value) < maxAttempt;

  const withLimit = useCallback(
    (redirect: () => void) => {
      if (!shouldRedirect) {
        return;
      }

      setValue((value) => {
        if (!hasValue(value)) {
          return '1';
        }
        const newValue = toNumber(value) + 1;
        return newValue.toString();
      });
      redirect();
    },
    [setValue, shouldRedirect]
  );

  return [shouldRedirect, withLimit] as const;
}

/** Reset the count of attempts of authentication. */
export function useResetAuthenticateCount() {
  const [, , remove] = useLocalStorage(AUTH_REDIRECT_ATTEMPT_LOCALSTORAGE_KEY, '0');

  useEffect(() => {
    remove();
  }, [remove]);
}
